import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { returnCasesHttp } from '@/services/returnCases'
import { toast } from '@/store/toastStore'
import type { ReviewWorkspace } from '@/types/returnCase.types'

export interface ReviewReadinessClient {
  reviewWorkspace: typeof returnCasesHttp.reviewWorkspace
  submitReviewSnapshot?: typeof returnCasesHttp.submitReviewSnapshot
  acceptReviewAssignment?: typeof returnCasesHttp.acceptReviewAssignment
  decideReview?: typeof returnCasesHttp.decideReview
  createReviewException?: typeof returnCasesHttp.createReviewException
  decideReviewException?: typeof returnCasesHttp.decideReviewException
  signFinalDeclaration?: typeof returnCasesHttp.signFinalDeclaration
  evaluateReadiness?: typeof returnCasesHttp.evaluateReadiness
  markReadyToFile?: typeof returnCasesHttp.markReadyToFile
  beginFinalDeclarationStepUp?: (
    email: string
  ) => Promise<{ challenge_id: string; message: string }>
  verifyFinalDeclarationStepUp?: (challengeId: string, otp: string) => Promise<unknown>
}

interface Props {
  caseId: string
  actorUserId?: string
  client?: ReviewReadinessClient
  onChanged?: () => Promise<void> | void
}

const stringify = (value: unknown) =>
  typeof value === 'string' ? value : JSON.stringify(value, null, 2)

export default function ReviewReadinessPanel({
  caseId,
  actorUserId,
  client = returnCasesHttp,
  onChanged,
}: Props) {
  const queryClient = useQueryClient()
  const [selectedRegime, setSelectedRegime] = useState<'OLD' | 'NEW'>('NEW')
  const [makerNote, setMakerNote] = useState('Submitted after final preparation checks.')
  const [reviewRationale, setReviewRationale] = useState('Reviewed against the current snapshot.')
  const [returnFinding, setReturnFinding] = useState('')
  const [declarationAccepted, setDeclarationAccepted] = useState(false)
  const [selectedExceptions, setSelectedExceptions] = useState<string[]>([])
  const [stepUpEmail, setStepUpEmail] = useState('')
  const [stepUpChallenge, setStepUpChallenge] = useState<string | null>(null)
  const [stepUpOtp, setStepUpOtp] = useState('')
  const [stepUpVerified, setStepUpVerified] = useState(false)

  const query = useQuery({
    queryKey: ['review-readiness', caseId],
    queryFn: () => client.reviewWorkspace(caseId),
    enabled: Boolean(caseId),
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['review-readiness', caseId] })
    await onChanged?.()
  }
  const mutation = useMutation({
    mutationFn: (operation: () => Promise<unknown>) => operation(),
    onSuccess: async () => {
      await refresh()
      toast.success('Review workspace updated')
    },
    onError: async error => {
      await refresh()
      toast.error(
        'Governed action rejected',
        error instanceof Error ? error.message : 'Review the current snapshot and blockers.'
      )
    },
  })
  const stepUpMutation = useMutation({
    mutationFn: (operation: () => Promise<unknown>) => operation(),
    onError: error =>
      toast.error(
        'Identity verification failed',
        error instanceof Error ? error.message : 'Request a new code and try again.'
      ),
  })

  if (query.isLoading)
    return <div className="mt-6 text-text-secondary">Loading governed review…</div>
  if (query.isError || !query.data)
    return (
      <section className="card mt-6 p-5" aria-label="Review and readiness">
        <div className="alert alert-error" role="alert">
          Governed review is unavailable or outside your permitted case scope.
        </div>
      </section>
    )

  const workspace: ReviewWorkspace = query.data
  const snapshot = workspace.snapshot
  const evaluation = workspace.latest_evaluation
  const actions = new Set(workspace.allowed_actions)
  const preReviewBlockers = Array.isArray(snapshot?.manifest.pre_review_blockers)
    ? (snapshot.manifest.pre_review_blockers as Array<Record<string, unknown>>)
    : []
  const changeSummary = snapshot?.manifest.review_change_summary as
    | Record<string, unknown>
    | undefined
  const changedAreas = Array.isArray(changeSummary?.changed_areas)
    ? changeSummary.changed_areas.map(String)
    : []
  const run = (operation: () => Promise<unknown>) => mutation.mutate(operation)

  return (
    <section className="card mt-6 space-y-5 p-5" aria-label="Review and readiness">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Governed review and Ready to File</h2>
          <p className="text-sm text-text-secondary">
            The server evaluates readiness. This workspace does not submit or e-verify a return.
          </p>
        </div>
        <span
          className={`badge ${workspace.readiness_state === 'READY_TO_FILE' ? 'badge-success' : 'badge-secondary'}`}
          aria-label={`Readiness state ${workspace.readiness_state}`}
        >
          {workspace.readiness_state}
        </span>
      </header>

      {actions.has('submit_review') && (
        <div className="rounded border border-border p-4">
          <h3 className="font-medium">Maker submission</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-[180px_1fr_auto]">
            <label className="text-sm">
              Selected regime
              <select
                className="input mt-1 w-full"
                value={selectedRegime}
                onChange={event => setSelectedRegime(event.target.value as 'OLD' | 'NEW')}
              >
                <option value="OLD">Old regime</option>
                <option value="NEW">New regime</option>
              </select>
            </label>
            <label className="text-sm">
              Submission note
              <input
                className="input mt-1 w-full"
                value={makerNote}
                onChange={event => setMakerNote(event.target.value)}
              />
            </label>
            <button
              className="btn btn-primary self-end"
              disabled={mutation.isPending || makerNote.trim().length < 3}
              onClick={() =>
                run(() =>
                  client.submitReviewSnapshot!(caseId, {
                    expected_revision: workspace.case_revision,
                    selected_regime: selectedRegime,
                    maker_note: makerNote.trim(),
                  })
                )
              }
            >
              Submit immutable snapshot
            </button>
          </div>
        </div>
      )}

      {snapshot && (
        <article className="rounded border border-border p-4" aria-label="Current review snapshot">
          <div className="flex flex-wrap justify-between gap-2">
            <h3 className="font-medium">Snapshot #{snapshot.snapshot_number}</h3>
            <span className={`badge ${snapshot.current ? 'badge-success' : 'badge-warning'}`}>
              {snapshot.current ? 'Current' : 'Stale'}
            </span>
          </div>
          <p className="mt-2 break-all font-mono text-xs">{snapshot.snapshot_hash}</p>
          <p className="mt-2 text-sm text-text-secondary">
            Revision {snapshot.case_revision} · maker {snapshot.maker_role} · selected regime{' '}
            {String(
              (snapshot.manifest.review_selection as Record<string, unknown> | undefined)?.regime ??
                'not recorded'
            )}
          </p>
          <div className="mt-3 rounded bg-surface-secondary p-3 text-sm">
            <p className="font-medium">Snapshot change summary</p>
            <p className="mt-1 text-text-secondary">
              {changeSummary?.kind === 'REPLACEMENT_SUBMISSION'
                ? `Replaces snapshot ${String(changeSummary.supersedes_snapshot_id ?? '')}`
                : 'Initial maker submission'}
            </p>
            <p className="mt-1">
              Changed manifest areas: {changedAreas.length ? changedAreas.join(', ') : 'none'}
            </p>
          </div>
          <details className="mt-3 rounded border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Inspect immutable review manifest
            </summary>
            <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs">
              {stringify(snapshot.manifest)}
            </pre>
          </details>
        </article>
      )}

      {snapshot && actions.has('assign_review') && actorUserId && (
        <button
          className="btn btn-secondary"
          disabled={mutation.isPending || !snapshot.current}
          onClick={() =>
            run(() =>
              client.acceptReviewAssignment!(caseId, snapshot.id, {
                expected_revision: workspace.case_revision,
                reviewer_user_id: actorUserId,
                conflict_attested: true,
                conflict_codes: [],
                reason: 'I accept this review and attest that I have no conflict.',
              })
            )
          }
        >
          Accept reviewer assignment with conflict attestation
        </button>
      )}

      {snapshot && workspace.assignment && actions.has('review_snapshot') && (
        <div className="rounded border border-border p-4">
          <h3 className="font-medium">Reviewer decision</h3>
          <label className="mt-3 block text-sm">
            Rationale
            <textarea
              className="input mt-1 min-h-20 w-full"
              value={reviewRationale}
              onChange={event => setReviewRationale(event.target.value)}
            />
          </label>
          <label className="mt-3 block text-sm">
            Anchored return finding
            <input
              className="input mt-1 w-full"
              value={returnFinding}
              onChange={event => setReturnFinding(event.target.value)}
              placeholder="Describe the correction and its case anchor"
            />
          </label>
          {workspace.exceptions.length > 0 && (
            <fieldset className="mt-3 space-y-2">
              <legend className="text-sm font-medium">Exceptions used by approval</legend>
              {workspace.exceptions.map(item => (
                <label key={item.id} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedExceptions.includes(item.id)}
                    disabled={item.status !== 'APPROVED'}
                    onChange={event =>
                      setSelectedExceptions(values =>
                        event.target.checked
                          ? [...values, item.id]
                          : values.filter(value => value !== item.id)
                      )
                    }
                  />
                  {item.blocker_code} · {item.status} · expires {item.expires_at.slice(0, 10)}
                </label>
              ))}
            </fieldset>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              className="btn btn-secondary"
              disabled={mutation.isPending || returnFinding.trim().length < 3}
              onClick={() =>
                run(() =>
                  client.decideReview!(caseId, snapshot.id, {
                    expected_revision: workspace.case_revision,
                    displayed_snapshot_hash: snapshot.snapshot_hash,
                    decision: 'RETURNED',
                    rationale: reviewRationale,
                    findings: [
                      {
                        code: 'REVIEW_CORRECTION_REQUIRED',
                        anchor_type: 'CASE',
                        anchor_ref: caseId,
                        severity: 'BLOCKER',
                        message: returnFinding.trim(),
                      },
                    ],
                    approved_exception_ids: [],
                    conflict_attested: true,
                  })
                )
              }
            >
              Return with anchored finding
            </button>
            <button
              className="btn btn-primary"
              disabled={mutation.isPending || !snapshot.current}
              onClick={() =>
                run(() =>
                  client.decideReview!(caseId, snapshot.id, {
                    expected_revision: workspace.case_revision,
                    displayed_snapshot_hash: snapshot.snapshot_hash,
                    decision:
                      selectedExceptions.length > 0 ? 'APPROVED_WITH_EXCEPTION' : 'APPROVED',
                    rationale: reviewRationale,
                    findings: [],
                    approved_exception_ids: selectedExceptions,
                    conflict_attested: true,
                  })
                )
              }
            >
              Approve exact snapshot
            </button>
          </div>
        </div>
      )}

      {snapshot && actions.has('request_review_exception') && (
        <div className="rounded border border-border p-4">
          <h3 className="font-medium">Request a bounded exception</h3>
          <p className="mt-1 text-sm text-text-secondary">
            Only the exact waivable blockers captured by this snapshot are available.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {preReviewBlockers
              .filter(item => item.waivable === true)
              .map(item => {
                const code = String(item.code) as
                  | 'REQUIRED_DOCUMENT_MISSING'
                  | 'RECONCILIATION_UNRESOLVED'
                return (
                  <button
                    key={`${code}-${String(item.dependency_id)}`}
                    className="btn btn-secondary"
                    disabled={mutation.isPending}
                    onClick={() =>
                      run(() =>
                        client.createReviewException!(caseId, snapshot.id, {
                          expected_revision: workspace.case_revision,
                          exception_type:
                            code === 'REQUIRED_DOCUMENT_MISSING'
                              ? 'MISSING_DOCUMENT'
                              : 'RECONCILIATION_DIFFERENCE',
                          blocker_code: code,
                          scope_type: 'CASE',
                          scope_ref: caseId,
                          rationale:
                            'Requested for reviewer consideration with the evidence and scope recorded on this case.',
                          evidence_binding_ids: [],
                          risk_level: 'MEDIUM',
                          expires_at: new Date(Date.now() + 30 * 86400000).toISOString(),
                        })
                      )
                    }
                  >
                    Request {code}
                  </button>
                )
              })}
          </div>
        </div>
      )}

      {snapshot && actions.has('manage_review_exception') && (
        <div className="rounded border border-border p-4">
          <h3 className="font-medium">Structured exceptions</h3>
          <ul className="mt-3 space-y-2">
            {workspace.exceptions.map(item => (
              <li key={item.id} className="rounded bg-surface-secondary p-3 text-sm">
                <strong>{item.blocker_code}</strong> · {item.risk_level} · {item.status}
                <p>{item.rationale}</p>
                {item.status === 'PENDING' && (
                  <span className="mt-2 inline-flex gap-2">
                    <button
                      className="btn btn-secondary"
                      onClick={() =>
                        run(() =>
                          client.decideReviewException!(caseId, item.id, {
                            expected_revision: workspace.case_revision,
                            decision: 'APPROVED',
                            reason: 'Reviewed evidence and approved this bounded exception.',
                          })
                        )
                      }
                    >
                      Approve exception
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() =>
                        run(() =>
                          client.decideReviewException!(caseId, item.id, {
                            expected_revision: workspace.case_revision,
                            decision: 'REJECTED',
                            reason: 'Exception evidence or rationale is insufficient.',
                          })
                        )
                      }
                    >
                      Reject exception
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {workspace.final_declaration && (
        <article
          className="rounded border border-border p-4"
          aria-label="Final customer declaration"
        >
          <h3 className="font-medium">Final preparation declaration</h3>
          <p className="mt-2 text-sm">{workspace.final_declaration.wording_text}</p>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            {Object.entries(workspace.final_declaration.summary).map(([key, value]) => (
              <div key={key}>
                <dt className="text-text-secondary">{key.split('_').join(' ')}</dt>
                <dd className="break-words">{stringify(value)}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 break-all font-mono text-xs">
            Summary hash {workspace.final_declaration.summary_hash}
          </p>
          {actions.has('sign_final_declaration') && !workspace.final_declaration.current && (
            <div className="mt-4 space-y-3">
              {client.beginFinalDeclarationStepUp &&
                client.verifyFinalDeclarationStepUp &&
                !stepUpVerified && (
                  <fieldset className="space-y-3 rounded border border-border p-3">
                    <legend className="px-1 text-sm font-medium">
                      Fresh identity verification
                    </legend>
                    <label className="block text-sm">
                      Email address
                      <input
                        type="email"
                        className="input mt-1 w-full"
                        autoComplete="email"
                        value={stepUpEmail}
                        onChange={event => setStepUpEmail(event.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={!stepUpEmail || stepUpMutation.isPending}
                      onClick={() =>
                        stepUpMutation.mutate(async () => {
                          const response = await client.beginFinalDeclarationStepUp!(stepUpEmail)
                          setStepUpChallenge(response.challenge_id)
                          setStepUpOtp('')
                          return response
                        })
                      }
                    >
                      Send verification code
                    </button>
                    {stepUpChallenge && (
                      <div className="flex flex-wrap items-end gap-2">
                        <label className="text-sm">
                          Six-digit code
                          <input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            className="input mt-1 block"
                            maxLength={6}
                            value={stepUpOtp}
                            onChange={event => setStepUpOtp(event.target.value)}
                          />
                        </label>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={!/^\d{6}$/.test(stepUpOtp) || stepUpMutation.isPending}
                          onClick={() =>
                            stepUpMutation.mutate(async () => {
                              await client.verifyFinalDeclarationStepUp!(stepUpChallenge, stepUpOtp)
                              setStepUpVerified(true)
                            })
                          }
                        >
                          Verify identity
                        </button>
                      </div>
                    )}
                  </fieldset>
                )}
              {stepUpVerified && (
                <p className="alert alert-success" role="status">
                  Identity verified for this declaration.
                </p>
              )}
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={declarationAccepted}
                  onChange={event => setDeclarationAccepted(event.target.checked)}
                />
                I accept this exact preparation summary. A fresh identity verification is required.
              </label>
              <button
                className="btn btn-primary"
                disabled={
                  !declarationAccepted ||
                  mutation.isPending ||
                  (Boolean(client.beginFinalDeclarationStepUp) && !stepUpVerified)
                }
                onClick={() =>
                  run(() =>
                    client.signFinalDeclaration!(caseId, snapshot!.id, {
                      expected_revision: workspace.case_revision,
                      displayed_snapshot_hash: snapshot!.snapshot_hash,
                      displayed_summary_hash: workspace.final_declaration!.summary_hash,
                      wording_version: '1.0.0',
                      accepted: true,
                    }).then(result => {
                      setStepUpVerified(false)
                      return result
                    })
                  )
                }
              >
                Sign final preparation declaration
              </button>
            </div>
          )}
        </article>
      )}

      {evaluation && (
        <div aria-live="polite" className="rounded border border-border p-4">
          <h3 className="font-medium">
            Server readiness evaluation #{evaluation.evaluation_number}
          </h3>
          {evaluation.blockers.length === 0 ? (
            <p className="mt-2 text-success">No current blockers.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {evaluation.blockers.map(item => (
                <li
                  key={`${item.code}-${item.dependency_id ?? ''}`}
                  className="alert alert-warning"
                >
                  <div>
                    <strong>{item.code}</strong> · owner {item.owner}
                    <p>{item.message}</p>
                    <a className="link" href={item.remediation_route}>
                      Open remediation
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {snapshot && (actions.has('evaluate_readiness') || actions.has('mark_ready_to_file')) && (
        <div className="flex flex-wrap gap-2">
          {actions.has('evaluate_readiness') && (
            <button
              className="btn btn-secondary"
              onClick={() =>
                run(() =>
                  client.evaluateReadiness!(caseId, {
                    expected_revision: workspace.case_revision,
                    snapshot_id: snapshot.id,
                    displayed_snapshot_hash: snapshot.snapshot_hash,
                  })
                )
              }
            >
              Evaluate server readiness
            </button>
          )}
          {actions.has('mark_ready_to_file') && (
            <button
              className="btn btn-primary"
              onClick={() =>
                run(() =>
                  client.markReadyToFile!(caseId, {
                    expected_revision: workspace.case_revision,
                    snapshot_id: snapshot.id,
                    displayed_snapshot_hash: snapshot.snapshot_hash,
                  })
                )
              }
            >
              Mark Ready to File
            </button>
          )}
        </div>
      )}
    </section>
  )
}
